import * as React from "react";
import { Helmet } from "react-helmet-async";
import { useQueryClient } from "react-query";
import type { RouteObject } from "react-router-dom";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import { constants, isDev } from "config";
import {
  SignIn,
  requireAuthentication,
  requireAuthorization,
  useAuth,
  useWalletAuth,
} from "features/auth";
import { LayoutFull, LayoutSite, LayoutTask } from "features/layouts";
import { scrollToTop, asyncScrollToHash } from "utils/scroll";
import { safeDynamicImport } from "utils/routing";
import { usePageTracking } from "contexts/mixpanel";

// Lazy imports for code-splitting based on routes, alphabetized by name
const About = safeDynamicImport(
  () => import("./features/corporate/components/About")
);
const AdminCreatePost = safeDynamicImport(
  () => import("./pages/AdminCreatePost")
);
const AboutArtistProfile = safeDynamicImport(
  () => import("./pages/ArtistProfileBenefits")
);
const AdminHomePageEditHero = safeDynamicImport(
  () => import("./pages/AdminHomePageEditHero")
);
const AdminHomePageEditDiscoverTag = safeDynamicImport(
  () => import("./pages/AdminHomePageEditDiscoverTag")
);
const AdminHomePageEditHighlight = safeDynamicImport(
  () => import("./pages/AdminHomePageEditHighlight")
);
const AdminHomePageEditFeaturedArtist = safeDynamicImport(
  () => import("./pages/AdminHomePageEditFeaturedArtist")
);
const AdminHomePagePublish = safeDynamicImport(
  () => import("./pages/AdminHomePagePublish")
);

const AdminHUGPointGrant = safeDynamicImport(
  () => import("./pages/AdminHUGPointGrant")
);
const AdminTicketDetails = safeDynamicImport(
  () => import("./pages/AdminTicketDetails")
);
const AdminTicketList = safeDynamicImport(
  () => import("./features/tickets/components/AdminTicketList")
);
const AdminUserDetails = safeDynamicImport(
  () => import("./pages/AdminUserDetails")
);
const AdminWrapper = safeDynamicImport(
  () => import("./features/admin/components/AdminWrapper")
);
const AllNotifications = safeDynamicImport(
  () => import("./features/notifications/components/AllNotifications")
);
const ArtistApplication = safeDynamicImport(
  () => import("./features/admin/components/ArtistApplication")
);
const ArtistApplications = safeDynamicImport(
  () => import("./features/admin/components/ArtistApplications")
);
const ArtistCreatePost = safeDynamicImport(
  () => import("./pages/ArtistCreatePost")
);
const ArtistDetails = safeDynamicImport(() => import("./pages/ArtistDetails"));
const ArtistInvites = safeDynamicImport(() => import("./pages/ArtistInvites"));
const ArtistPost = safeDynamicImport(() => import("./pages/ArtistPost"));
const ArtistPostEdit = safeDynamicImport(
  () => import("./pages/ArtistPostEdit")
);
const ArtistUpdatesArchive = safeDynamicImport(
  () => import("./pages/ArtistUpdatesArchive")
);
const ArtistFollowersPage = safeDynamicImport(
  () => import("./pages/ArtistFollowersPage")
);
const ArtistVoting = safeDynamicImport(() => import("./pages/ArtistVoting"));
const BannerMaker = safeDynamicImport(() => import("./pages/BannerMaker"));
const ClaimArtistProfile = safeDynamicImport(
  () => import("./features/reviewable/components/ClaimArtistProfile")
);
const CollectiveAllPosts = safeDynamicImport(
  () => import("./features/posts/components/CollectiveAllPosts")
);
const CollectiveAdminCreate = safeDynamicImport(
  () => import("./pages/CollectiveAdminCreate")
);
const CollectiveAdminDetails = safeDynamicImport(
  () => import("./pages/CollectiveAdminDetails")
);
const CollectiveAdminEdit = safeDynamicImport(
  () => import("./pages/CollectiveAdminEdit")
);
const CollectiveAdminList = safeDynamicImport(
  () => import("./pages/CollectiveAdminList")
);
const CollectiveDetails = safeDynamicImport(
  () => import("./pages/CollectiveDetails")
);
const CollectiveList = safeDynamicImport(
  () => import("./pages/CollectiveList")
);
const Commissions = safeDynamicImport(
  () => import("./features/search/components/Commissions")
);
const CreateAccountEmail = safeDynamicImport(
  () => import("./pages/CreateAccountEmail")
);
const CreateArtist = safeDynamicImport(() => import("./pages/CreateArtist"));
const CreateArtistApplication = safeDynamicImport(
  () => import("./features/reviewable/components/CreateArtistApplication")
);
const CreateArtistReview = safeDynamicImport(
  () => import("./pages/CreateArtistReview")
);
const CreateProject = safeDynamicImport(() => import("./pages/CreateProject"));
const CreateProjectReview = safeDynamicImport(
  () => import("./pages/CreateProjectReview")
);
const CuratorReport = safeDynamicImport(
  () => import("./features/admin/components/CuratorReport")
);
const CurationTasks = safeDynamicImport(
  () => import("./features/profile/components/CurationTasks")
);
const DeiInitiatives = safeDynamicImport(
  () => import("./features/corporate/components/DeiInitiatives")
);
const Dev = safeDynamicImport(() => import("./pages/Dev"));
const EditArtist = safeDynamicImport(() => import("./pages/EditArtist"));
const EditArtistApplication = safeDynamicImport(
  () => import("./features/reviewable/components/EditArtistApplication")
);
const EditArtistReview = safeDynamicImport(
  () => import("./pages/EditArtistReview")
);
const EditProfile = safeDynamicImport(
  () => import("./features/profile/components/EditProfile")
);
const EditProject = safeDynamicImport(() => import("./pages/EditProject"));
const EditProjectReview = safeDynamicImport(
  () => import("./pages/EditProjectReview")
);
const FAQ = safeDynamicImport(
  () => import("./features/corporate/components/FAQ")
);
const ForgotPassword = safeDynamicImport(
  () => import("./pages/ForgotPassword")
);
const ResourceLibrary = safeDynamicImport(
  () => import("./pages/ResourceLibrary")
);
const HubHome = safeDynamicImport(() => import("./pages/HubHome"));
const InviteCreator = safeDynamicImport(
  () => import("./features/reviewable/components/InviteCreator")
);
const MintStatusReport = safeDynamicImport(
  () => import("./features/qa/components/MintStatusReport")
);
const MobileSignIn = safeDynamicImport(
  () => import("./features/profile/components/MobileSignIn")
);
const NotFound = safeDynamicImport(() => import("./pages/NotFound"));
const Onboarding = safeDynamicImport(() => import("./pages/Onboarding"));
const OpenCallAdminCreate = safeDynamicImport(
  () => import("./pages/OpenCallAdminCreate")
);
const OpenCallAdminDetails = safeDynamicImport(
  () => import("./pages/OpenCallAdminDetails")
);
const OpenCallAdminEdit = safeDynamicImport(
  () => import("./pages/OpenCallAdminEdit")
);
const OpenCallAdminList = safeDynamicImport(
  () => import("./pages/OpenCallAdminList")
);
const OpenCallDetails = safeDynamicImport(
  () => import("./pages/OpenCallDetails")
);
const OpenCallList = safeDynamicImport(() => import("./pages/OpenCallList"));
const OpenCallSubmissionCreate = safeDynamicImport(
  () => import("./pages/OpenCallSubmissionCreate")
);
const OpenCallSubmissionEdit = safeDynamicImport(
  () => import("./pages/OpenCallSubmissionEdit")
);
const OpenCallVoting = safeDynamicImport(
  () => import("./pages/OpenCallVoting")
);
const PrivacyPolicy = safeDynamicImport(
  () => import("./features/corporate/components/PrivacyPolicy")
);
const ProjectApplicationForm = safeDynamicImport(
  () => import("./features/reviewable/components/ProjectApplicationForm")
);
const ProjectApplication = safeDynamicImport(
  () => import("./features/admin/components/ProjectApplication")
);
const ProjectApplications = safeDynamicImport(
  () => import("./features/admin/components/ProjectApplications")
);
const ProfilePage = safeDynamicImport(
  () => import("./features/profile/components/ProfilePage")
);
const ProjectDetails = safeDynamicImport(
  () => import("./pages/ProjectDetails")
);
const QAWrapper = safeDynamicImport(
  () => import("./features/qa/components/QAWrapper")
);
const RaffleReport = safeDynamicImport(
  () => import("./features/admin/components/RaffleReport")
);
const ReviewModeration = safeDynamicImport(
  () => import("./pages/ReviewModeration")
);
const RewardsShop = safeDynamicImport(() => import("./pages/RewardsShop"));
const Search = safeDynamicImport(
  () => import("./features/search/components/Search")
);
const SupportPage = safeDynamicImport(
  () => import("./features/corporate/components/SupportPage")
);
const TermsOfService = safeDynamicImport(
  () => import("./features/corporate/components/TermsOfService")
);
const ResetPassword = safeDynamicImport(() => import("./pages/ResetPassword"));
const UserProfile = safeDynamicImport(() => import("./pages/UserProfile"));
const UserSearch = safeDynamicImport(
  () => import("./features/admin/components/UserSearch")
);
const ViewAllUpdates = safeDynamicImport(
  () => import("./pages/ViewAllUpdates")
);

/**
 * Global router configuration object
 */
const routes: RouteObject[] = [
  {
    Component: LayoutSite,
    children: [
      { index: true, Component: HubHome },
      { path: "*", Component: NotFound },
      { path: "about", Component: About },
      { path: "artist-application", Component: CreateArtistApplication },
      { path: "artist-invites", Component: ArtistInvites },
      { path: "artist-profile-benefits", Component: AboutArtistProfile },
      { path: "artist-updates", Component: ViewAllUpdates },
      { path: "banner-maker", Component: BannerMaker },
      { path: "commissions", Component: Commissions },
      { path: "dei-initiatives", Component: DeiInitiatives },
      { path: "faq", Component: FAQ },
      {
        path: "for-creators",
        element: <Redirect url={constants.studiosUrl} />,
      },
      { path: "privacy-policy", Component: PrivacyPolicy },
      { path: "project-application", Component: ProjectApplicationForm },
      { path: "redeem-hug-points", Component: RewardsShop },
      { path: "search", Component: Search },
      { path: "support", Component: SupportPage },
      { path: "terms-of-service", Component: TermsOfService },
      {
        path: "admin",
        Component: requireAuthorization(AdminWrapper, {
          feature: "showAdminFeatures",
        }),
        children: [
          { path: "reports", Component: CuratorReport },
          { path: "raffles", Component: RaffleReport },
          { path: "applications", Component: ArtistApplications },
          {
            path: "applications/:applicationId/edit",
            Component: EditArtistApplication,
          },
          {
            path: "applications/:applicationId",
            Component: ArtistApplication,
          },
          {
            path: "project-applications",
            Component: ProjectApplications,
          },
          {
            path: "project-applications/:applicationId",
            Component: ProjectApplication,
          },
          {
            path: "collectives",
            children: [
              {
                index: true,
                Component: CollectiveAdminList,
              },
              {
                path: "new",
                Component: CollectiveAdminCreate,
              },
              {
                path: ":collectiveId",
                children: [
                  {
                    index: true,
                    Component: CollectiveAdminDetails,
                  },
                  {
                    path: "edit",
                    Component: CollectiveAdminEdit,
                  },
                ],
              },
            ],
          },
          {
            path: "open-calls",
            children: [
              {
                index: true,
                Component: OpenCallAdminList,
              },
              {
                path: ":openCallId",
                children: [
                  {
                    index: true,
                    Component: OpenCallAdminDetails,
                  },
                  {
                    path: "edit",
                    Component: OpenCallAdminEdit,
                  },
                ],
              },
              {
                path: "new",
                Component: OpenCallAdminCreate,
              },
            ],
          },
          {
            path: "/admin/create-update",
            Component: AdminCreatePost,
          },
          { path: "users", Component: UserSearch },
          {
            path: "users/:userId",
            Component: AdminUserDetails,
          },
          {
            path: "edit-home-hero",
            Component: AdminHomePageEditHero,
          },
          {
            path: "edit-home-featured-artist",
            Component: AdminHomePageEditFeaturedArtist,
          },
          {
            path: "edit-home-discover-tag",
            Component: AdminHomePageEditDiscoverTag,
          },
          {
            path: "edit-home-highlight",
            Component: AdminHomePageEditHighlight,
          },
          {
            path: "edit-home-publish",
            Component: AdminHomePagePublish,
          },
          {
            path: "hug-points",
            Component: AdminHUGPointGrant,
          },
        ],
      },
      {
        path: "qa",
        Component: requireAuthorization(QAWrapper, {
          feature: "showQAFeatures",
        }),
        children: [
          { path: "mint-status", Component: MintStatusReport },
          { path: "tickets", Component: AdminTicketList },
          {
            path: "tickets/:ticketId",
            Component: AdminTicketDetails,
          },
        ],
      },
      {
        path: "moderation",
        Component: requireAuthorization(ReviewModeration, {
          feature: "canModerate",
        }),
      },
      {
        path: "artist-voting",
        Component: requireAuthorization(ArtistVoting, { roles: ["isHugger"] }),
      },
      {
        path: "artists",
        children: [
          { path: ":artistId", Component: ArtistDetails },
          {
            path: "create",
            Component: requireAuthorization(CreateArtist, {
              feature: "showAdminFeatures",
            }),
          },
          {
            path: ":artistId/edit",
            Component: requireAuthentication(EditArtist, {
              createAccount: true,
            }),
          },
          {
            path: ":artistId/review",
            Component: requireAuthentication(CreateArtistReview, {
              createAccount: true,
            }),
          },
          {
            path: ":artistId/reviews/:reviewedByUserId/edit",
            Component: requireAuthentication(EditArtistReview, {
              createAccount: true,
            }),
          },
          {
            path: ":artistId/updates",
            Component: ArtistUpdatesArchive,
          },
          {
            path: ":artistId/updates/:postId",
            Component: ArtistPost,
          },
          {
            path: ":artistId/updates/:postId/edit",
            Component: ArtistPostEdit,
          },
          {
            path: ":artistId/create-update",
            Component: requireAuthorization(ArtistCreatePost, {
              feature: "canPost",
            }),
          },
          {
            path: ":artistId/followers",
            Component: ArtistFollowersPage,
          },
        ],
      },
      {
        path: "projects",
        children: [
          { path: ":projectId", Component: ProjectDetails },
          {
            path: "create",
            Component: requireAuthorization(CreateProject, {
              feature: "showAdminFeatures",
            }),
          },
          {
            path: ":projectId/edit",
            Component: requireAuthorization(EditProject, {
              feature: "showAdminFeatures",
            }),
          },
          {
            path: ":projectId/review",
            Component: requireAuthentication(CreateProjectReview, {
              createAccount: true,
            }),
          },
          {
            path: ":projectId/reviews/:reviewedByUserId/edit",
            Component: requireAuthentication(EditProjectReview, {
              createAccount: true,
            }),
          },
        ],
      },
      {
        path: "profile",
        children: [
          { index: true, Component: requireAuthentication(ProfilePage) },
          { path: "edit", Component: requireAuthentication(EditProfile) },
          {
            path: "mobileSignIn",
            Component: requireAuthentication(MobileSignIn),
          },
          {
            path: "notifications",
            Component: requireAuthentication(AllNotifications),
          },
          { path: "curate", Component: requireAuthentication(CurationTasks) },
        ],
      },
      {
        path: "users",
        children: [{ path: ":userId", Component: UserProfile }],
      },
      {
        path: "collectives",
        children: [
          { index: true, Component: CollectiveList },
          { path: ":slug", Component: CollectiveDetails },
          { path: ":slug/updates", Component: CollectiveAllPosts },
        ],
      },
      {
        path: "badges",
        children: [{ path: ":slug", Component: CollectiveDetails }],
      },
      {
        path: "open-calls",
        children: [
          { index: true, Component: OpenCallList },
          {
            path: ":openCallId",
            children: [
              { index: true, Component: OpenCallDetails },
              {
                path: "submit",
                Component: requireAuthentication(OpenCallSubmissionCreate),
              },
              {
                path: "vote",
                Component: requireAuthentication(OpenCallVoting),
              },
              {
                path: ":createdByUserId/edit",
                Component: requireAuthentication(OpenCallSubmissionEdit),
              },
            ],
          },
        ],
      },
      {
        path: "resources",
        children: [
          { index: true, Component: ResourceLibrary },
          { path: ":resourceSlug", Component: ResourceLibrary },
        ],
      },
    ],
  },
  {
    Component: LayoutTask,
    children: [
      {
        path: "/artist-invite/:artistId",
        Component: requireAuthorization(InviteCreator, {
          feature: "showAdminFeatures",
        }),
      },
      {
        path: "/artist-claim/:artistId/:inviteCode",
        Component: ClaimArtistProfile,
      },
    ],
  },
  {
    Component: LayoutFull,
    children: [
      // NOTE: this is helpful for testing components or CSS while developing
      ...(isDev ? [{ path: "/dev", Component: Dev }] : []),
      // NOTE: SignIn component is rendered by two different paths for small change in copy
      { path: "/sign-in", element: <SignIn /> },
      { path: "/register", Component: CreateAccountEmail },
      { path: "/create-account", element: <SignIn isCreateAccount /> },
      { path: "/forgot-password", element: <ForgotPassword /> },
      {
        path: "/reset-password",
        Component: requireAuthentication(ResetPassword),
      },
      // NOTE: This route should only be routed to programmatically after SignIn process
      { path: "/onboarding", Component: requireAuthentication(Onboarding) },
    ],
  },
];

function Redirect({ url }: { url: string }) {
  React.useEffect(() => {
    window.location.href = url;
  }, [url]);
  return null;
}

const resetPasswordRoute = "/reset-password";
const onboardingRoute = "/onboarding";

function Router() {
  const queryClient = useQueryClient();
  const {
    signOut,
    user,
    roles: { isCollector, isCurator },
  } = useAuth();
  const { disconnect } = useWalletAuth();
  const location = useLocation();
  const navigate = useNavigate();

  usePageTracking();

  // Check user object for any forced tasks
  React.useEffect(() => {
    let forceRoute = "";

    if (
      user?.forcePassword &&
      !location.pathname.startsWith(resetPasswordRoute)
    ) {
      forceRoute = resetPasswordRoute;
    } else if (
      user?.forceOnboarding &&
      !isCollector &&
      !isCurator &&
      !location.pathname.startsWith(onboardingRoute)
    ) {
      forceRoute = onboardingRoute;
    }

    if (forceRoute) {
      navigate(forceRoute, { state: { from: location } });
    }
  }, [user, location, navigate, isCollector, isCurator]);

  React.useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        // If any query resolves in an error that indicates the token is invalid, use methods from
        // Auth provider to sign them out which would allow a clean slate to sign back in and get
        // a new valid token
        onError: async (error) => {
          if (
            error instanceof Error &&
            error.message.includes("Authorization token is invalid")
          ) {
            disconnect();
            signOut();
          }
        },
      },
    });
  }, [queryClient, signOut, disconnect]);

  // Scroll on all transitions
  React.useLayoutEffect(() => {
    if (location.hash && location.hash !== "#" && location.hash !== "") {
      // Try to find hash and scroll to it, waiting for loads if necessary
      asyncScrollToHash();
    } else {
      // Path changed and/or hash was removed, scroll to top
      scrollToTop();
    }
  }, [location.hash, location.pathname]);

  return (
    <>
      <Helmet>
        <link rel="canonical" href={`https://thehug.xyz${location.pathname}`} />
      </Helmet>
      {useRoutes(routes)}
    </>
  );
}

export { Router, routes };
